import { TBC_DATE, addMinutes } from "@/lib/bookingLabels";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import type {
  Booking,
  BookingCategory,
  BookingStatus,
  Client,
  PaymentMethod,
  PaymentStatus,
} from "@/types/database";

export type BookingWithClient = Booking & {
  clients: Pick<
    Client,
    "id" | "venue_or_client_name" | "contact_name" | "email" | "phone"
  > | null;
};

export type BookingFormInput = {
  venue_or_client_name: string;
  contact_name: string;
  email: string;
  /** Second address that also gets payment links and reminders */
  extra_email?: string;
  phone: string;
  performance_date: string;
  start_time: string;
  end_time: string;
  duration_minutes: string;
  address: string;
  category: BookingCategory;
  internal_notes: string;
  client_description: string;
  fee_pounds: string;
  due_date: string;
  preferred_payment_method: PaymentMethod;
  booking_status: BookingStatus;
  payment_status: PaymentStatus;
  venue_postcode: string;
  distance_miles: string;
  /** Fuel charge in £, added on top of the performance fee */
  fuel_pounds: string;
  /** Date not yet confirmed */
  date_tbc?: boolean;
  /** Existing client id when editing */
  client_id?: string;
};

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; unconfigured?: boolean };

function unconfigured<T>(): ApiResult<T> {
  return {
    ok: false,
    error: "Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    unconfigured: true,
  };
}

/** Map PostgREST / Postgres errors to actionable admin copy. */
export function mapBookingApiError(message: string): string {
  const m = message || "";
  if (
    m.includes("PGRST205") ||
    m.includes("schema cache") ||
    /Could not find the table ['"]?public\.(clients|bookings|admin_profiles)/i.test(m)
  ) {
    return (
      `${m} — Database tables are missing. In Supabase SQL Editor, run ` +
      `supabase/ensure_booking_schema.sql (see docs/fix-bookings-schema.md), then retry Save.`
    );
  }
  if (m.includes("row-level security") || m.toLowerCase().includes("rls")) {
    return (
      `${m} — Your Auth user needs a row in public.admin_profiles. ` +
      `Re-run the admin seed section of ensure_booking_schema.sql.`
    );
  }
  return m;
}

function poundsToPence(raw: string): number {
  const n = Number.parseFloat(raw);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t ? t : null;
}

export async function listBookings(opts?: {
  includeCancelled?: boolean;
  upcomingOnly?: boolean;
}): Promise<ApiResult<BookingWithClient[]>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  let q = supabase
    .from("bookings")
    .select(
      "*, clients ( id, venue_or_client_name, contact_name, email, phone )"
    )
    .order("performance_date", { ascending: true })
    .order("start_time", { ascending: true, nullsFirst: true });

  if (!opts?.includeCancelled) {
    q = q.neq("booking_status", "cancelled");
  }
  if (opts?.upcomingOnly) {
    const today = new Date().toISOString().slice(0, 10);
    q = q.gte("performance_date", today).eq("booking_status", "scheduled");
  }

  const { data, error } = await q;
  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  return { ok: true, data: (data ?? []) as BookingWithClient[] };
}

export async function getBooking(id: string): Promise<ApiResult<BookingWithClient>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  const { data, error } = await supabase
    .from("bookings")
    .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
    .eq("id", id)
    .maybeSingle();

  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  if (!data) return { ok: false, error: "Booking not found." };
  return { ok: true, data: data as BookingWithClient };
}

export async function findOverlaps(input: {
  performance_date: string;
  start_time?: string | null;
  end_time?: string | null;
  excludeId?: string;
}): Promise<ApiResult<BookingWithClient[]>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  let q = supabase
    .from("bookings")
    .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
    .eq("performance_date", input.performance_date)
    .eq("booking_status", "scheduled");

  if (input.excludeId) q = q.neq("id", input.excludeId);

  const { data, error } = await q;
  if (error) return { ok: false, error: mapBookingApiError(error.message) };

  const rows = (data ?? []) as BookingWithClient[];
  // If no times given, any same-day scheduled booking is an overlap warning
  if (!input.start_time && !input.end_time) {
    return { ok: true, data: rows };
  }

  const start = input.start_time || "00:00";
  const end = input.end_time || "23:59";

  const overlaps = rows.filter((b) => {
    const bStart = b.start_time?.slice(0, 5) || "00:00";
    const bEnd = b.end_time?.slice(0, 5) || "23:59";
    return start < bEnd && end > bStart;
  });

  return { ok: true, data: overlaps };
}

async function upsertClient(
  input: BookingFormInput,
  userId: string | undefined
): Promise<ApiResult<string>> {
  if (!supabase) return unconfigured();

  if (input.client_id) {
    const { error } = await supabase
      .from("clients")
      .update({
        venue_or_client_name: input.venue_or_client_name.trim(),
        contact_name: emptyToNull(input.contact_name),
        email: emptyToNull(input.email),
        phone: emptyToNull(input.phone),
      })
      .eq("id", input.client_id);
    if (error) return { ok: false, error: mapBookingApiError(error.message) };
    return { ok: true, data: input.client_id };
  }

  const { data, error } = await supabase
    .from("clients")
    .insert({
      venue_or_client_name: input.venue_or_client_name.trim(),
      contact_name: emptyToNull(input.contact_name),
      email: emptyToNull(input.email),
      phone: emptyToNull(input.phone),
      created_by: userId ?? null,
    })
    .select("id")
    .single();

  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  return { ok: true, data: data.id as string };
}

function bookingPayload(input: BookingFormInput, clientId: string, userId?: string) {
  const duration = input.duration_minutes.trim()
    ? Number.parseInt(input.duration_minutes, 10)
    : null;

  return {
    client_id: clientId,
    performance_date: input.date_tbc ? TBC_DATE : input.performance_date,
    date_tbc: !!input.date_tbc,
    start_time: input.date_tbc ? null : emptyToNull(input.start_time),
    end_time: input.date_tbc
      ? null
      : input.start_time && Number.isFinite(duration) && duration
        ? addMinutes(input.start_time, duration as number)
        : emptyToNull(input.end_time),
    duration_minutes: Number.isFinite(duration) ? duration : null,
    address: emptyToNull(input.address),
    category: input.category,
    internal_notes: emptyToNull(input.internal_notes),
    client_description: emptyToNull(input.client_description),
    extra_email: emptyToNull(input.extra_email ?? ""),
    fee_pence: poundsToPence(input.fee_pounds) + poundsToPence(input.fuel_pounds || "0"),
    fuel_charge_pence: poundsToPence(input.fuel_pounds || "0"),
    venue_postcode: emptyToNull((input.venue_postcode ?? "").trim().toUpperCase()),
    distance_miles: input.distance_miles ? Number(input.distance_miles) : null,
    currency: "gbp" as const,
    due_date: emptyToNull(input.due_date),
    preferred_payment_method: input.preferred_payment_method,
    booking_status: input.booking_status,
    payment_status: input.payment_status,
    created_by: userId ?? null,
  };
}

export async function createBooking(
  input: BookingFormInput,
  userId?: string
): Promise<ApiResult<BookingWithClient>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();
  if (!input.venue_or_client_name.trim()) {
    return { ok: false, error: "Venue / client name is required." };
  }
  if (!input.performance_date && !input.date_tbc) {
    return { ok: false, error: "Add a date, or tick Date TBC." };
  }

  const clientRes = await upsertClient(input, userId);
  if (clientRes.ok === false) {
    return { ok: false, error: clientRes.error, unconfigured: clientRes.unconfigured };
  }

  const payload = bookingPayload(input, clientRes.data, userId);
  // `reference` is assigned by DB trigger (OF-YYYY-NNN)
  const { data, error } = await supabase
    .from("bookings")
    .insert(payload)
    .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
    .single();

  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  return { ok: true, data: data as BookingWithClient };
}

export async function updateBooking(
  id: string,
  input: BookingFormInput,
  userId?: string
): Promise<ApiResult<BookingWithClient>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  const clientRes = await upsertClient(input, userId);
  if (clientRes.ok === false) {
    return { ok: false, error: clientRes.error, unconfigured: clientRes.unconfigured };
  }

  const { created_by: _c, ...payload } = bookingPayload(input, clientRes.data, userId);
  void _c;

  const { data, error } = await supabase
    .from("bookings")
    .update(payload)
    .eq("id", id)
    .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
    .single();

  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  return { ok: true, data: data as BookingWithClient };
}

export async function setBookingStatus(
  id: string,
  booking_status: BookingStatus,
  extra?: { cancel_reason?: string }
): Promise<ApiResult<Booking>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  const patch: Record<string, unknown> = { booking_status };
  if (booking_status === "cancelled") {
    patch.cancelled_at = new Date().toISOString();
    patch.cancel_reason = extra?.cancel_reason ?? null;
  }
  if (booking_status === "scheduled") {
    patch.cancelled_at = null;
    patch.cancel_reason = null;
  }

  const { data, error } = await supabase
    .from("bookings")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();

  if (error) return { ok: false, error: mapBookingApiError(error.message) };
  return { ok: true, data: data as Booking };
}

export function bookingToForm(b: BookingWithClient): BookingFormInput {
  return {
    venue_or_client_name: b.clients?.venue_or_client_name ?? "",
    contact_name: b.clients?.contact_name ?? "",
    email: b.clients?.email ?? "",
    phone: b.clients?.phone ?? "",
    performance_date: (b as { date_tbc?: boolean }).date_tbc ? "" : b.performance_date,
    date_tbc: !!(b as { date_tbc?: boolean }).date_tbc,
    start_time: b.start_time?.slice(0, 5) ?? "",
    end_time: b.end_time?.slice(0, 5) ?? "",
    duration_minutes:
      b.duration_minutes?.toString() ??
      (b.start_time && b.end_time
        ? String(
            (Number(b.end_time.slice(0, 2)) * 60 + Number(b.end_time.slice(3, 5))) -
              (Number(b.start_time.slice(0, 2)) * 60 + Number(b.start_time.slice(3, 5)))
          )
        : ""),
    address: b.address ?? "",
    category: b.category,
    internal_notes: b.internal_notes ?? "",
    client_description: b.client_description ?? "",
    extra_email: (b as { extra_email?: string | null }).extra_email ?? "",
    fee_pounds: ((b.fee_pence - (b.fuel_charge_pence ?? 0)) / 100).toFixed(2),
    fuel_pounds: ((b.fuel_charge_pence ?? 0) / 100).toFixed(2),
    venue_postcode: b.venue_postcode ?? "",
    distance_miles: b.distance_miles != null ? String(b.distance_miles) : "",
    due_date: b.due_date ?? "",
    preferred_payment_method: b.preferred_payment_method ?? "stripe",
    booking_status: b.booking_status,
    payment_status: b.payment_status,
    client_id: b.client_id,
  };
}

export function emptyBookingForm(defaultDueDays = 7): BookingFormInput {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 7);
  const performance_date = tomorrow.toISOString().slice(0, 10);
  const due = new Date(tomorrow);
  due.setDate(due.getDate() - defaultDueDays);

  return {
    venue_or_client_name: "",
    contact_name: "",
    email: "",
    phone: "",
    performance_date,
    start_time: "14:00",
    end_time: "15:00",
    duration_minutes: "60",
    address: "",
    category: "care_home",
    internal_notes: "",
    client_description: "",
    fee_pounds: "150.00",
    fuel_pounds: "0.00",
    venue_postcode: "",
    distance_miles: "",
    due_date: due.toISOString().slice(0, 10),
    preferred_payment_method: "stripe",
    booking_status: "scheduled",
    payment_status: "awaiting",
  };
}

export type DashboardStats = {
  upcoming: number;
  awaitingPayment: number;
  paidThisMonth: number;
  needsAttention: number;
  totalRevenuePence: number;
};

export function computeDashboardStats(rows: BookingWithClient[]): DashboardStats {
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;

  let upcoming = 0;
  let awaitingPayment = 0;
  let paidThisMonth = 0;
  let needsAttention = 0;
  let totalRevenuePence = 0;

  for (const b of rows) {
    if (b.booking_status === "scheduled" && b.performance_date >= today) {
      upcoming += 1;
    }
    if (b.booking_status !== "cancelled" && b.payment_status === "awaiting") {
      awaitingPayment += 1;
    }
    if (b.payment_status === "attention" && b.booking_status !== "cancelled") {
      needsAttention += 1;
    }
    if (b.payment_status === "paid") {
      totalRevenuePence += b.fee_pence;
      // Approximate "paid this month" by updated_at until payments table is used in Stage 5/8
      if (b.updated_at.slice(0, 10) >= monthStart) {
        paidThisMonth += 1;
      }
    }
  }

  return { upcoming, awaitingPayment, paidThisMonth, needsAttention, totalRevenuePence };
}

/** An extra performance date added alongside the main booking. */
export type ExtraDate = {
  performance_date: string;
  date_tbc?: boolean;
  start_time: string;
  end_time: string;
  duration_minutes?: string;
  address: string;
  fee_pounds: string;
};

/**
 * Create the main booking plus extra dates for the same client.
 * When extras exist, all bookings are linked by a booking group so the
 * client can pay them together (group link) or individually (own links).
 */
export async function createBookingsWithExtras(
  input: BookingFormInput,
  extras: ExtraDate[],
  userId?: string
): Promise<ApiResult<{ first: BookingWithClient; groupId: string | null }>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();
  const valid = extras.filter((e) => e.performance_date || e.date_tbc);
  if (valid.length === 0) {
    const r = await createBooking(input, userId);
    if (r.ok === false) return r;
    return { ok: true, data: { first: r.data, groupId: null } };
  }
  if (!input.venue_or_client_name.trim()) {
    return { ok: false, error: "Venue / client name is required." };
  }
  const clientRes = await upsertClient(input, userId);
  if (clientRes.ok === false) return clientRes;

  const { data: group, error: gErr } = await supabase
    .from("booking_groups")
    .insert({ client_id: clientRes.data, created_by: userId ?? null })
    .select("id")
    .single();
  if (gErr) return { ok: false, error: mapBookingApiError(gErr.message) };

  const rows = [
    { ...bookingPayload(input, clientRes.data, userId), group_id: group.id },
    ...valid.map((e) => ({
      ...bookingPayload(
        {
          ...input,
          performance_date: e.performance_date,
          date_tbc: !!e.date_tbc,
          start_time: e.start_time,
          end_time: e.end_time,
          address: e.address || input.address,
          fee_pounds: e.fee_pounds || input.fee_pounds,
          duration_minutes: e.duration_minutes ?? "",
        },
        clientRes.data,
        userId
      ),
      group_id: group.id,
    })),
  ];
  // Insert one by one so the reference trigger numbers them in order
  let first: BookingWithClient | null = null;
  for (const row of rows) {
    const { data, error } = await supabase
      .from("bookings")
      .insert(row)
      .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
      .single();
    if (error) return { ok: false, error: mapBookingApiError(error.message) };
    if (!first) first = data as BookingWithClient;
  }
  return { ok: true, data: { first: first!, groupId: group.id as string } };
}

export async function listGroupBookings(
  groupId: string
): Promise<ApiResult<{ pay_token: string; bookings: BookingWithClient[] }>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();
  const [g, b] = await Promise.all([
    supabase.from("booking_groups").select("pay_token").eq("id", groupId).single(),
    supabase
      .from("bookings")
      .select("*, clients ( id, venue_or_client_name, contact_name, email, phone )")
      .eq("group_id", groupId)
      .order("performance_date"),
  ]);
  if (g.error) return { ok: false, error: g.error.message };
  if (b.error) return { ok: false, error: b.error.message };
  return {
    ok: true,
    data: { pay_token: g.data.pay_token as string, bookings: (b.data ?? []) as BookingWithClient[] },
  };
}
